import { ContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IExternalCenterTransfer } from '@agent/core/contact/transfer/models/external-transfer';
import { ITransfer, TransferTarget, TransferType } from '@agent/core/contact/transfer/models/transfer';
import { TransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { DirectoryService } from '@agent/core/search/directory.service';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NG1_CONTACT, NG1_DIRECTORY, NG1_Q, NG1_TRANSFER } from '@commons/core/core.provider.ajs';
import { ChannelUtil } from '@commons/util/helper/channel';
import { RxUtil } from '@commons/util/utilities/rx';
import { IPromise, IQService, isArray, isDefined } from 'angular';
import { map, BehaviorSubject, debounceTime, repeat, skipWhile, take, delay } from 'rxjs';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';

import {
  AgentUserConfService,
  Channel,
  CSR_FACADE_PREFIX,
  ElasticSearchService,
  Entity,
  Logger,
  MetricId,
  MetricService,
  None,
  Option,
  ProfileFacade,
  ProfileService,
  TranslateService,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

type FilterType = 'group' | 'team' | 'skill';

const DISABLE_DM2_AVAILABILITY_CHECK = false;

interface IAgentFilter extends Entity {
  id: string;
  name: string;
  type: FilterType;
}

@UntilDestroy()
@Component({
  selector: 'chat-transfer-workspace',
  templateUrl: 'chat-transfer-workspace.component.html',
  styleUrls: ['./chat-transfer-workspace.component.scss'],
})
export class ChatTransferWorkspaceComponent implements OnInit, OnDestroy {
  TransferTarget = TransferTarget;

  @Input()
  responseId: string;

  @Input()
  isConference: boolean;

  availableSkills: any[] = [];
  availableTeams: IAgentFilter[] = [];
  externalCenterToTransfer: any[] = [];
  agentFilterType: string = 'teamOrGroup';
  availableSkillFilters: IAgentFilter[] = [];
  availableTargetTypes: TransferTarget[] = [];
  teamOrGroupFilter: IAgentFilter;
  skillsFilter: IAgentFilter[] = [];
  noResultsAgentTarget: boolean = false;
  noResultsTeamTarget: boolean = false;
  resultsAgentTarget: IAgentFilter[] = [];
  public skillsResetSwitch: boolean = false;
  public waitForWSResponse: boolean = true;
  public waitForWSDomainSkillResponse: boolean = true;
  private _domainId: string;
  private currentCsrUuid: string;

  haveGroupFilter = false;
  availableGroupFilters: IAgentFilter[] = [];
  haveTeamFilter = false;
  availableTeamFilters: IAgentFilter[] = [];

  protected readonly _agentChosen$ = new BehaviorSubject<string>('');
  readonly agentChosen$ = this._agentChosen$.asObservable();

  constructor(
    private readonly logger: Logger,
    private readonly translateService: TranslateService,
    protected readonly profileService: ProfileService,
    protected readonly agentUserConfService: AgentUserConfService,
    @Inject(NG1_CONTACT) protected readonly contactDeprecatedService: ContactDeprecatedService,
    @Inject(NG1_TRANSFER) protected readonly transferDeprecatedService: TransferDeprecatedService,
    @Inject(NG1_DIRECTORY) protected readonly directoryService: DirectoryService,
    private readonly profileFacade: ProfileFacade,
    protected readonly elasticSearchService: ElasticSearchService,
    @Inject(NG1_Q) protected readonly $q: IQService,
    protected readonly metricService: MetricService,
    private readonly httpDeprecated: HttpDeprecatedService
  ) {
    this.profileFacade.id$.pipe(untilDestroyed(this)).subscribe(id => (this.currentCsrUuid = id));
  }

  get transfer(): ITransfer {
    let transfer = this.transferDeprecatedService
      .getTransfer(this.responseId)
      .getOrElseFunc(() => this.transferDeprecatedService.createTransfer(this.responseId, ChannelUtil.INCOMING_CHAT));
    if (this.isConference) {
      transfer.targetType = TransferTarget.AGENT;
    }
    if (transfer.targetType === TransferTarget.SKILL || transfer.targetType === TransferTarget.TEAM) {
      transfer.type = TransferType.NOT_ACCOMPANIED;
    } else {
      transfer.type = TransferType.ACCOMPANIED;
    }
    return transfer;
  }

  get isDialingTarget(): boolean {
    return this.transferDeprecatedService.isDialingTarget(this.responseId);
  }

  ngOnInit(): void {
    if (this.profileService.hasPermission('CSR_CHAT_INCOMING::contact:transfer_to_skill') && !this.isConference) {
      this.availableTargetTypes.push(TransferTarget.SKILL);
    }
    if (this.profileService.hasPermission('CSR_CHAT_INCOMING::contact:transfer_to_team') && !this.isConference) {
      this.availableTargetTypes.push(TransferTarget.TEAM);
      this.noResultsTeamTarget = false;
    }
    if (this.profileService.hasPermission('CSR_CHAT_INCOMING::contact:transfer')) {
      this.availableTargetTypes.push(TransferTarget.AGENT);
      this.noResultsAgentTarget = false;
    }

    this.transferDeprecatedService.retriveTransferTec().subscribe(
      (next: IExternalCenterTransfer) => {
        this.externalCenterToTransfer = next.externalCenterToTransfer;
      },
      error => {
        this.logger.error(error);
      }
    );

    this.transferDeprecatedService
      .getAvailableSkills(this.responseId)
      .then(skills => {
        this.availableSkills = [...skills].sort((a, b) => {
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          return 0;
        });
      })
      .finally(() => {
        this.waitForWSResponse = false;
      });

    this.agentUserConfService.getDomainGroups().subscribe(groups => {
      if (groups.length > 0) {
        this.haveGroupFilter = true;
        groups.forEach(group => {
          this.availableGroupFilters.push({
            id: group.id,
            name: group.name,
            type: 'group',
          });
        });
        this.availableGroupFilters.sort((g1, g2) => g1.name.localeCompare(g2.name));
      }
    });

    this.agentUserConfService.getDomainTeams().subscribe(teams => {
      if (teams.length > 0) {
        this.haveTeamFilter = true;
        teams.forEach(team => {
          this.availableTeamFilters.push({
            id: team.id,
            name: team.name,
            type: 'team',
          });
        });
        this.availableTeamFilters.sort((t1, t2) => t1.name.localeCompare(t2.name));
      }
    });

    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      // retrieve skill filtering for current contact channel
      this.agentUserConfService
        .getDomainSkillsForChannel({
          type: contact.channel.type,
          direction: contact.channel.direction,
        })
        .subscribe(skills => {
          skills.forEach(skill =>
            this.availableSkillFilters.push({
              id: skill.id,
              name: skill.name,
              type: 'skill',
            })
          );
          this.availableSkillFilters.sort((s1, s2) => s1.name.localeCompare(s2.name));
          this.waitForWSDomainSkillResponse = false;
        });
    });

    this.profileFacade.profile$.pipe(untilDestroyed(this)).subscribe(profile => (this._domainId = profile.domainId));

    this.getAgents();
    this._agentChosen$.pipe(debounceTime(500)).subscribe(csrPrefix => this.getAgents(csrPrefix));
  }

  ngOnDestroy(): void {
    this._agentChosen$.complete();
  }

  onTargetSkillChange(data: any): void {
    if (data) {
      Array.isArray(data) ? (this.skillsFilter = data) : this.skillsFilter.push(data);
      this.noResultsAgentTarget = false;
      this.getAgents();
    } else {
      this.noResultsAgentTarget = false;
      this.skillsFilter = [];
    }
  }

  onTargetTeamOrGroupChange(): void {
    this.noResultsAgentTarget = false;
    this.getAgents();
  }

  onTargetChange(): void {
    let transfer = this.transfer;
    this.noResultsAgentTarget = false;
    this.noResultsTeamTarget = false;

    transfer.target = '';
    this.transferDeprecatedService.updateTransfer(transfer);
    this.skillsFilter = [];
    this.resultsAgentTarget = [];
    this.updateSkillAvailability(this.transfer.targetType === TransferTarget.TEAM);
    if (this.agentFilterType === 'teamOrGroup') {
      this.getAgents();
    }
  }

  onCsrChange(data: string): void {
    this._agentChosen$.next(data);
  }

  private updateSkillAvailability(isTeam: boolean): void {
    if (isTeam) {
      this.availableSkills.forEach(s => {
        if (!s.agentAvailableOnDomain) {
          s.cssClass = 'red';
        } else {
          s.cssClass = 'green';
        }
      });
    } else {
      this.availableSkills.forEach(s => {
        if (!s.agentAvailable) {
          s.cssClass = 'red';
        } else {
          s.cssClass = 'green';
        }
      });
    }
  }

  getTargetLabel(target: TransferTarget): string {
    let targetKey = TransferTarget[target].toLowerCase() + '_target';
    return this.translateService.instant('agent:contact.workspace.phone.transfer.' + targetKey);
  }

  getAgents(text?: string) {
    let groupFilter = None;
    let teamFilter = None;

    if (this.skillsFilter.length === 0) {
      let filter = Option.of(this.teamOrGroupFilter);
      groupFilter = filter.filter(f => f.type === 'group');
      teamFilter = filter.filter(f => f.type === 'team');
    }

    this.directoryService
      .getInternalCorrespondents(this._domainId, groupFilter, teamFilter, this.skillsFilter, text, ChannelUtil.INCOMING_CHAT)
      .then(success => {
        if (success.data.length !== 0) {
          let unfilteredCsr;
          this.getAgentWithAvailableSkillsForAGivenChannel(success.data, ChannelUtil.INCOMING_CHAT, this.currentCsrUuid)
            .then((users: any) => {
              unfilteredCsr = users;
              return this.filterCsrBySlotAvailability(unfilteredCsr);
            })
            .catch(exception => {
              this.logger.error(`Error while calling DM2 : ${exception}`);
              return unfilteredCsr;
            })
            .then(users => {
              if (users && users.length !== 0) {
                this.noResultsAgentTarget = false;
                this.resultsAgentTarget = users;
              } else {
                this.noResultsAgentTarget = true;
                this.resultsAgentTarget = [];
              }
            });
        } else {
          this.noResultsAgentTarget = true;
          this.resultsAgentTarget = [];
        }
      });
  }

  private filterCsrBySlotAvailability(users: any[]) {
    if (DISABLE_DM2_AVAILABILITY_CHECK) {
      return users;
    }

    if (users.length === 0) {
      return [];
    }
    return this.callGetCsrBySlotAvailabilityAuthenticated(users);
  }

  private callGetCsrBySlotAvailabilityAuthenticated(users: any[]): IPromise<any> {
    const userUuids: string[] = users.map((user: any) => user.id);
    return this.httpDeprecated
      .asPromise()
      .withData(userUuids)
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
          Accept: 'application/json',
        },
      })
      .post(CSR_FACADE_PREFIX + '/getCsrBySlotAvailabilityAuthenticated')
      .then(result => {
        if (result == null) {
          return this.$q.reject();
        }
        if (result.data.length === 0) {
          return [];
        }
        return users.filter(user => result.data.some(idFiltered => idFiltered === user.id));
      });
  }

  private buildAgent(_source: any, phoneNumber: string, channelType: string, channelDirection: string): any {
    return {
      id: _source.uuid,
      name: `${_source.firstName} ${_source.lastName}`,
      phoneNumber: phoneNumber,
      skills: _source.skills
        .filter((skill: any) => skill.mediaDirection === channelDirection && skill.mediaType === channelType)
        .map((skill: any) => ({
          id: skill.uuid,
          name: skill.name,
        })),
    };
  }

  private getAgentWithAvailableSkillsForAGivenChannel(agents: any, channel: Channel, excludedCsrUuid: String): IPromise<any> {
    let deferred = this.$q.defer<any>();
    const agentsUuids = agents.map((agent: any) => agent.uuid);
    const searchParams: any = {
      types: ['user'],
      size: 50,
      query: {
        bool: {
          must: [{ terms: { uuid: agentsUuids } }, { term: { isConnected: true } }],
          mustNot: [{ term: { uuid: excludedCsrUuid } }],
        },
      },
    };

    RxUtil.toPromise(this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams), this.$q).then(
      success => {
        if (
          !!success &&
          isDefined(success.hits) &&
          isDefined(success.hits.total) &&
          isDefined(success.hits.total.value) &&
          success.hits.total.value > 0 &&
          isArray(success.hits.hits)
        ) {
          const agentsWithSkills = success.hits.hits.map(agent => {
            const phoneNumber = agents.filter(elt => elt.uuid === agent._source.uuid)[0].phoneNumber;
            return this.buildAgent(agent._source, phoneNumber, channel.type, channel.direction);
          });

          // Remove the agents which don't have any skill
          const agentsWithSkillsFiltered = agentsWithSkills.filter((agent: any) => agent.skills.length > 0);

          // Check if the agents have the skills activated if we are filtering by skills
          if (this.skillsFilter.length > 0) {
            const skillsFilterUuid = this.skillsFilter.map((skill: any) => skill.id);
            deferred.resolve(
              agentsWithSkillsFiltered.filter((agent: any) => {
                const skillsUserUuid = agent.skills.map((skill: any) => skill.id);
                return skillsFilterUuid.every(skill => skillsUserUuid.includes(skill));
              })
            );
          } else {
            deferred.resolve(agentsWithSkillsFiltered);
          }
        } else {
          deferred.reject('[chat-transfer] cannot find csrs');
        }
      },
      () => deferred.reject('[chat-transfer] Error on searchOnSeveralTypesWithPromise in getAgentWithAvailableSkillsForAGivenChannel')
    );

    return deferred.promise;
  }

  updateCandidateTeamList(data: any): void {
    this.skillsFilter = [];
    if (data != null) {
      Array.isArray(data) ? (this.skillsFilter = data) : this.skillsFilter.push(data);
    }
    this.transfer.target = null;
    this.noResultsTeamTarget = false;
    this.availableTeams = [];

    let unfilteredTeams;
    this.directoryService
      // Get the teams of the domain having a csr with the given skill (with available csr or not)
      .getInternalCorrespondentsWithTeam(this._domainId, this.skillsFilter, '', ChannelUtil.INCOMING_CHAT)
      .then(success =>
        Object.values(
          success.data
            .map((elsUser: any): any => this.mapToTeamOption(elsUser))
            .reduce((newMap, teamOption) => {
              newMap[teamOption.id] = teamOption;
              return newMap;
            }, {})
        )
      ) // distinct teams
      // Filter to get only the teams with a least one available slot
      .then(teamOptions => {
        unfilteredTeams = teamOptions;
        return this.filterTeamsBySlotAvailability(this.currentCsrUuid, unfilteredTeams);
      })
      .catch(exception => {
        this.logger.error(`Error while calling DM2 : ${exception}`);
        this.availableTeams = unfilteredTeams;
        this.noResultsTeamTarget = unfilteredTeams.length === 0;
        return unfilteredTeams;
      })
      // Refresh the display of the teams and set the "No teams" message if necessary
      .then(availableTeams => {
        this.availableTeams = availableTeams;
        //Case where DM2 metric values are not yet created and calculated
        if (availableTeams.length === 0 && unfilteredTeams.length > 0) {
          let maxRetry = 3;
          this.getTeamFromMetricValue(this.currentCsrUuid, unfilteredTeams)
            .pipe(
              delay(200),
              repeat(maxRetry),
              skipWhile((response: any, idx) => idx + 1 < maxRetry && response.length === 0),
              take(1),
              untilDestroyed(this)
            )
            .subscribe(result => {
              this.availableTeams = result;
              this.noResultsTeamTarget = result.length === 0;
            });
        } else {
          this.noResultsTeamTarget = availableTeams.length === 0;
        }
      })
      .finally(() => (this.skillsResetSwitch = false));
  }

  private mapToTeamOption(elsUser: any): any {
    if (elsUser?.teams?.length > 0) {
      return {
        id: elsUser.teams[0].uuid,
        name: elsUser.teams[0].name,
        type: 'team',
        skills: this.skillsFilter,
      };
    }
    return {};
  }

  private filterTeamsBySlotAvailability(agtId: string, teamOptions: any[]) {
    if (DISABLE_DM2_AVAILABILITY_CHECK) {
      return teamOptions;
    }

    if (teamOptions.length === 0) {
      return [];
    }
    let metricValues$ = this.getTeamFromMetricValue(agtId, teamOptions);
    return RxUtil.toPromise(metricValues$, this.$q);
  }

  private getTeamFromMetricValue(agtId: string, teamOptions: any[]) {
    // Create a map[metricName -> teamUuid]
    let metricIdMap: { [key: string]: MetricId } = teamOptions.reduce((newMap, teamOpt) => {
      newMap[teamOpt.id] = this.createMetricForTeamAvailabilityMetric(agtId, teamOpt);
      return newMap;
    }, {});
    let metricNameMap = teamOptions.reduce((newMap, teamOpt) => {
      newMap[metricIdMap[teamOpt.id].toString()] = teamOpt.id;
      return newMap;
    }, {});

    // Retrieve the metric values from DM2 and filter the teams to keep only those with at least one slot
    let metricValues$ = this.metricService
      .getMetrics(Object.values(metricIdMap))
      .pipe(
        map(metricValues =>
          metricValues.reduce((newMap, metric) => {
            newMap[metricNameMap[metric.metricId]] = metric.value;
            return newMap;
          }, {})
        )
      ) // to Map(metricId->Value)
      .pipe(map(metricValueMap => teamOptions.filter(teamOpt => metricValueMap[teamOpt.id] > 0)));
    return metricValues$;
  }

  private createMetricForTeamAvailabilityMetric(agtId: string, teamOption: any) {
    if (teamOption.skills && teamOption.skills.length > 0) {
      return MetricId.of('slot_nb_available_for_team_skill_domain_transfer_now')
        .addLiteralParam(agtId)
        .addLiteralParam(this._domainId)
        .addLiteralListParam([teamOption.id])
        .addLiteralParam(teamOption.skills[0].id)
        .addLiteralParam('CHAT')
        .addLiteralParam('INCOMING');
    } else {
      return MetricId.of('slot_nb_available_for_team_domain_transfer_now')
        .addLiteralParam(agtId)
        .addLiteralParam(this._domainId)
        .addLiteralListParam([teamOption.id])
        .addLiteralParam('CHAT')
        .addLiteralParam('INCOMING');
    }
  }

  updateTransferTarget(data: IAgentFilter) {
    if (this.transfer.targetType === TransferTarget.SKILL) {
      this.transfer.target = Array.isArray(data) ? [...data] : data;
      this.onTargetSkillChange(this.transfer.target);
    } else {
      this.transfer.target = data;
    }
  }

  resetTeamSkills(): void {
    this.skillsFilter = [];
    this.transfer.target = null;
    this.skillsResetSwitch = true;
  }
}
